import type {
    AnchorHTMLAttributes,
    ButtonHTMLAttributes,
    FC,
    ReactNode
} from 'react'

import Link from 'next/link'
import { cn } from '@/lib/utils'

type ButtonVariant = 'primary' | 'secondary' | 'dark'
type ButtonSize = 'sm' | 'md' | 'lg'

const VARIANTS: Record<ButtonVariant, string> = {
    primary: 'border-plum bg-butter text-plum shadow-[0_5px_0_0_var(--color-plum)] hover:shadow-[0_7px_0_0_var(--color-plum)] active:shadow-[0_1px_0_0_var(--color-plum)]',
    secondary: 'border-plum bg-cream text-plum shadow-[0_5px_0_0_var(--color-plum)] hover:shadow-[0_7px_0_0_var(--color-plum)] active:shadow-[0_1px_0_0_var(--color-plum)]',
    dark: 'border-butter bg-butter text-plum shadow-[0_5px_0_0_#B38A1F] hover:shadow-[0_7px_0_0_#B38A1F] active:shadow-[0_1px_0_0_#B38A1F]'
}

const SIZES: Record<ButtonSize, string> = {
    sm: 'h-10 px-4 text-base',
    md: 'h-12 px-6 text-lg',
    lg: 'h-14 px-8 text-xl'
}

export const buttonClasses = (variant: ButtonVariant = 'primary', size: ButtonSize = 'md', className?: string): string => {
    return cn(
        'inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-full border-2 font-heading tracking-wide transition-[translate,box-shadow,background-color] duration-200 ease-out hover:-translate-y-0.5 active:translate-y-1 disabled:pointer-events-none disabled:opacity-60',
        VARIANTS[variant],
        SIZES[size],
        className
    )
}

interface ButtonLinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
    href: string
    variant?: ButtonVariant
    size?: ButtonSize
}

export const ButtonLink: FC<ButtonLinkProps> = ({
    href,
    variant,
    size,
    className,
    children,
    ...props
}): ReactNode => {
    const classes = buttonClasses(variant, size, className)

    if (href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) {
        return (
            <a href={href} className={classes} {...props}>
                {children}
            </a>
        )
    }

    return (
        <Link href={href} className={classes} {...props}>
            {children}
        </Link>
    )
}

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: ButtonVariant
    size?: ButtonSize
}

export const Button: FC<ButtonProps> = ({
    variant,
    size,
    className,
    type = 'button',
    children,
    ...props
}): ReactNode => {
    return (
        <button type={type} className={buttonClasses(variant, size, className)} {...props}>
            {children}
        </button>
    )
}
